import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useNotificationPolling } from '../hooks/useNotificationPolling';
import { useNotificationWebSocket } from '../hooks/useNotificationWebSocket';
import { sendEmailNotification, sendSMSNotification } from '../utils/notifyChannels';
import { useSupabase, useAuth } from './SupabaseProvider';

export type NotificationType = 'success' | 'error' | 'info';

interface NotificationPreferences {
  email: boolean;
  sms: boolean;
  browser: boolean;
}

interface NotificationContextValue {
  notifications: [];
  addNotification: (message: string, type?: NotificationType, options?: NotificationOptions) => void;
  removeNotification: (id: number) => void;
  requestPermission: () => Promise<NotificationPermission>;
  preferences: NotificationPreferences;
  updatePreferences: (prefs: Partial<NotificationPreferences>) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const supabase = useSupabase();
  const { user } = useAuth();
  const recentNotificationKeysRef = useRef<Map<string, number>>(new Map());
  const [preferences, setPreferences] = useState<NotificationPreferences>(() => {
    const stored = localStorage.getItem('notification-preferences');
    const storedPreferences = stored ? JSON.parse(stored) : { email: true, sms: false, browser: true };
    return user?.user_metadata?.notification_preferences
      ? { ...storedPreferences, ...user.user_metadata.notification_preferences }
      : storedPreferences;
  });

  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return 'denied';
    if (Notification.permission === 'granted') return 'granted';
    try {
      const result = await Notification.requestPermission();
      return typeof result === 'string' ? result : Notification.permission;
    } catch (error) {
      console.warn('Notification permission request failed', error);
      return Notification.permission;
    }
  }, []);

  const removeNotification = useCallback(() => {
    // In-App-Toasts sind deaktiviert; die Methode bleibt für bestehende Aufrufer stabil.
  }, []);

  const addNotification = useCallback(
    (message: string, type: NotificationType = 'info', options?: NotificationOptions) => {
      const dedupKey = `${type}:${message}`;
      const now = Date.now();
      const lastSeen = recentNotificationKeysRef.current.get(dedupKey);

      if (lastSeen && now - lastSeen < 60000) {
        return;
      }

      recentNotificationKeysRef.current.set(dedupKey, now);
      setTimeout(() => {
        if (recentNotificationKeysRef.current.get(dedupKey) === now) {
          recentNotificationKeysRef.current.delete(dedupKey);
        }
      }, 60000);

      if (preferences.email) sendEmailNotification(message);
      if (preferences.sms) sendSMSNotification(message);

      // Geräte-Push kommt ausschließlich über Web Push aus dem Service Worker,
      // damit App-Polling und WebSocket keine doppelten Systembenachrichtigungen auslösen.
      void options;
    },
    [preferences.email, preferences.sms]
  );

  const updatePreferences = useCallback(
    (prefs: Partial<NotificationPreferences>) => {
      setPreferences((prev) => {
        const updated = { ...prev, ...prefs };
        localStorage.setItem('notification-preferences', JSON.stringify(updated));
        supabase.auth
          .updateUser({ data: { notification_preferences: updated } })
          .catch((e) => console.error('Failed to save notification preferences', e));
        return updated;
      });
    },
    [supabase]
  );

  // Poll backend for new notifications
  useNotificationPolling(addNotification);
  // Real-time notifications via WebSocket
  useNotificationWebSocket(addNotification);

  return (
    <NotificationContext.Provider value={{ notifications: [], addNotification, removeNotification, requestPermission, preferences, updatePreferences }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
